import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MiscellaneousServicesOutlinedIcon from '@mui/icons-material/MiscellaneousServicesOutlined';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import useTranslation from '../../../translations/use-translation';
import './CSCOwnerDashboard.styles.scss';

const getServices = () => {
  try { return JSON.parse(localStorage.getItem('cscServices') || '[]'); }
  catch { return []; }
};

const getProfile = () => {
  try { return JSON.parse(localStorage.getItem('cscOwnerProfile')) || {}; }
  catch { return {}; }
};

const norm = (v) => String(v || '').trim().toLowerCase();

// Applications submitted by users for this owner's center.
const getOwnerRequests = (profile) => {
  try {
    const all = JSON.parse(localStorage.getItem('userApplications') || '[]');
    return all.filter((app) => {
      const c = app.cscCenter || {};
      if (profile.id && c.cscCenterId && profile.id === c.cscCenterId) return true;
      if (profile.centerName && norm(c.cscCenterName) === norm(profile.centerName)) return true;
      if (profile.mobile && c.cscCenterMobile && profile.mobile === c.cscCenterMobile) return true;
      if (profile.email && norm(c.cscCenterEmail) === norm(profile.email)) return true;
      return false;
    });
  } catch { return []; }
};

const CSCOwnerDashboard = () => {
  const translate = useTranslation();
  const navigate  = useNavigate();
  const profile   = getProfile();
  const [services] = useState(getServices);
  const [requests] = useState(() => getOwnerRequests(profile));

  const pendingCount   = requests.filter((r) => r.serviceStatus === 'Applied' || r.serviceStatus === 'In Progress').length;
  const completedCount = requests.filter((r) => r.serviceStatus === 'Completed').length;

  const stats = [
    {
      labelKey:  'CSC_TOTAL_SERVICES',
      value:     services.length,
      icon:      <MiscellaneousServicesOutlinedIcon />,
      mod:       'services',
      clickable: true,
      onClick:   () => navigate('/csc-portal/services'),
    },
    {
      labelKey:  'CSC_TOTAL_REQUESTS',
      value:     requests.length,
      icon:      <InboxOutlinedIcon />,
      mod:       'requests',
      clickable: true,
      onClick:   () => navigate('/csc-portal/requests'),
    },
    { labelKey: 'CSC_PENDING',        value: pendingCount,   icon: <HourglassEmptyOutlinedIcon />, mod: 'pending'   },
    { labelKey: 'CSC_COMPLETED',      value: completedCount, icon: <CheckCircleOutlineIcon />,     mod: 'completed' },
  ];

  return (
    <div className="cod">

      {/* ── Welcome banner ─────────────────────────────────── */}
      <div className="cod__banner">
        <div className="cod__banner-text">
          <h1 className="cod__banner-title">
            {translate('CSC_WELCOME')}, {profile.ownerName || translate('CSC_OWNER')}
          </h1>
          <p className="cod__banner-sub">
            {profile.city && profile.state
              ? `${profile.city}, ${profile.state}`
              : translate('CSC_OWNER_PORTAL')}
          </p>
        </div>
        <div className="cod__banner-decoration" aria-hidden="true" />
      </div>

      {/* ── Stats grid ──────────────────────────────────────── */}
      <div className="cod__stats">
        {stats.map(({ labelKey, value, icon, mod, clickable, onClick }) => (
          <div
            key={labelKey}
            className={`cod__stat-card cod__stat-card--${mod}${clickable ? ' cod__stat-card--clickable' : ''}`}
            onClick={onClick}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onKeyDown={clickable ? (e) => e.key === 'Enter' && onClick?.() : undefined}
          >
            <span className={`cod__stat-icon cod__stat-icon--${mod}`}>{icon}</span>
            <div className="cod__stat-body">
              <span className="cod__stat-value">{value}</span>
              <span className="cod__stat-label">{translate(labelKey)}</span>
            </div>
            {clickable && <span className="cod__stat-view-badge">View all</span>}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CSCOwnerDashboard;
